import { IntegrationProvider } from "@repo/app-types/entities/integration";

// Integration metadata, in its own module so both the settings section and the
// breadcrumb label can read it without importing the section component.
export type IntegrationConfig = {
  provider: IntegrationProvider;
  slug: string;
  name: string;
  description: string;
  disconnectWarning: string;
};

/** The slug the (single) "Recording S3 intake" config uses for either intake provider. */
export const RECORDING_S3_INTAKE_SLUG = "recording-s3-intake";

export const NICE_CXONE_SLUG = "nice-cxone";

// Registry of the integrations rendered in the overview table and detail pages.
// Add an entry to surface a new integration.
export const integrationConfigs: IntegrationConfig[] = [
  {
    provider: IntegrationProvider.SLACK,
    slug: "slack",
    name: "Slack",
    description:
      "Connect Slack to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Slack will stop automation results from being delivered to Slack.",
  },
  {
    provider: IntegrationProvider.TEAMS,
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    description:
      "Connect Microsoft Teams to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Microsoft Teams will stop automation results from being delivered to Teams.",
  },
  {
    provider: IntegrationProvider.GURU,
    slug: "guru",
    name: "Guru",
    description:
      "Sync Guru collections into Alpharun knowledge bases so agents answer from the same articles your team does. Each collection becomes its own read-only knowledge base, kept up to date in the background.",
    disconnectWarning:
      "Disconnecting Guru will delete its synced knowledge bases and documents.",
  },
  {
    provider: IntegrationProvider.KUSTOMER,
    slug: "kustomer",
    name: "Kustomer",
    description:
      "Connect Kustomer so its knowledge bases are synced into read-only Alpharun knowledge bases.",
    disconnectWarning:
      "Disconnecting Kustomer will delete its synced knowledge bases and documents.",
  },
  {
    provider: IntegrationProvider.GENESYS,
    slug: "genesys",
    name: "Genesys Cloud",
    description:
      "Import Genesys Cloud call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Genesys Cloud will stop new call recordings from syncing.",
  },
  {
    provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
    slug: "zoom-contact-center",
    name: "Zoom Contact Center",
    description:
      "Import Zoom Contact Center call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Zoom Contact Center will stop new call recordings from syncing.",
  },
  {
    // One entry for both intake providers (`five9_recording_s3_intake` and
    // `ringcentral_recording_s3_intake`): a company has at most one, created
    // from HQ, and the overview matches on the slug rather than this provider
    // key. Shown only when the boot payload carries such a row. There is no
    // connect/disconnect — the overview special-cases it — so
    // `disconnectWarning` is never surfaced.
    provider: IntegrationProvider.FIVE9_RECORDING_S3_INTAKE,
    slug: RECORDING_S3_INTAKE_SLUG,
    name: "Recording S3 intake",
    description:
      "Call recordings delivered to your Alpharun S3 bucket are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
  },
  {
    // Alpharun-operated (scripts/setupNiceCxoneIntegration.ts): shown only when
    // the boot payload carries the company's row, with no connect/disconnect,
    // so `disconnectWarning` is never surfaced.
    provider: IntegrationProvider.NICE_CXONE,
    slug: NICE_CXONE_SLUG,
    name: "NICE CXone",
    description:
      "Call recordings from your NICE CXone account are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
  },
];

/** Resolves an integration's display name from its slug, or null if unknown. */
export function getIntegrationNameBySlug(
  slug: string | undefined,
): string | null {
  return (
    integrationConfigs.find((config) => config.slug === slug)?.name ?? null
  );
}
